import React, {FC} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Colors, Modal, RoundButton, Text, Title} from '@optimaxdev/design-system';
import {Approve, Copy, Icon} from '@optimaxdev/icons';

import {TYP_COUPON_DETAILS_CLICK} from 'constants/analytics/events/typ';
import {useCopyWithTimeout} from 'hooks/useCopyWithTimeout/useCopyWithTimeout';

import s from './oneTimeCouponModal.scss';

export type PropsType = {
    showCouponModal: boolean;
    onClose: () => void;
    couponCode: string;
};

/**
 * One time coupon modal to copy coupon code
 */
export const OneTimeCouponModal: FC<PropsType> = ({couponCode, onClose, showCouponModal}) => {
    const {copyText, isCopied} = useCopyWithTimeout();

    const handleCopy = () => {
        copyText(couponCode);
        sendDesktopAnalyticsEvent(TYP_COUPON_DETAILS_CLICK);
    };

    return (
        <Modal isOpen={showCouponModal} onRequestClose={onClose} overlay="dark">
            <section className={s.wrapper}>
                <Title
                    className={s.title}
                    color={Colors.black}
                    level={2}
                    margin="0 0 30px 0"
                    textAlign="center"
                >
                    While we start working on your order, enjoy this special treat.
                </Title>
                <div className={s.content}>
                    <strong className={s.couponPrice} aria-hidden>
                        15{' '}
                        <Text as="span" className={s.couponPriceOff} lineHeight="24px" size="16px">
                            $ off
                        </Text>
                    </strong>

                    <div>
                        <Title color={Colors.codGray} level={3} lineHeight="28px">
                            $15 off your next purchase
                        </Title>
                        <Text as="p" color={Colors.codGray} lineHeight="24px" size="16px">
                            {couponCode} is your unique code • Valid for 24 hours only • Exclusions
                            may apply
                        </Text>
                    </div>

                    <div className={s.couponCodeWrapper}>
                        <Text
                            as="p"
                            color={Colors.limedSpruce}
                            lineHeight="24px"
                            margin="0 0 14px 0"
                            size="14px"
                            light
                        >
                            Coupon code:
                            <strong className={s.couponCodeValue}>{couponCode}</strong>
                        </Text>

                        <RoundButton
                            startIcon={
                                <span className={isCopied ? s.checkIcon : s.copyIcon}>
                                    <Icon
                                        height={17}
                                        icon={isCopied ? Approve : Copy}
                                        strokeWidth={2}
                                        width={17}
                                    />
                                </span>
                            }
                            className={s.copyButton}
                            onClick={handleCopy}
                            size="small"
                            {...(isCopied ? {} : {onClick: handleCopy})}
                        >
                            {isCopied ? 'Copied!' : 'Copy Code'}
                        </RoundButton>
                    </div>
                </div>
                <Text
                    as="p"
                    className={s.couponDisclaimer}
                    lineHeight="20px"
                    margin="0 0 0 4px"
                    size="14px"
                >
                    Free shipping for US & Canada • Can be combined with existing discounts • Only
                    applicable with a new order • Not valid if order being processed is canceled
                </Text>
            </section>
        </Modal>
    );
};
